import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { DataTable, Input, Select } from "@hp/ui";
import type { DataTableColumn } from "@hp/ui";
import type { Finding } from "@hp/types";
import { listFindings } from "../data/index";
import { ErrorState, LoadingState } from "../components/Feedback";
import { formatDate, formatINR, humanizeForum, humanizeIssueTag } from "../lib/workbenchFormat";
import { FORUMS, ISSUE_TAGS } from "../lib/constants";

type ColumnKey = "petitioner_respondent" | "quantum_amount" | "quantum_basis" | "ratio_summary";

const OPTIONAL_COLUMNS: Array<{ key: ColumnKey; label: string }> = [
  { key: "petitioner_respondent", label: "Petitioner / Respondent" },
  { key: "quantum_amount", label: "Quantum amount" },
  { key: "quantum_basis", label: "Quantum basis" },
  { key: "ratio_summary", label: "Ratio summary" },
];

const DEFAULT_VISIBLE: Set<ColumnKey> = new Set(["quantum_amount", "quantum_basis"]);

/**
 * The concrete, bounded version of the PRD's "peer benchmarking" idea
 * (mike's own "tabular review" pattern): pick one normative parameter,
 * pick a set of comparable orders by issue tag, and see one row per
 * order with that field pulled out - not a general benchmarking engine,
 * just this one well-scoped view.
 */
export function TabularReviewPage() {
  const [issueTag, setIssueTag] = React.useState("");
  const [forum, setForum] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [visibleColumns, setVisibleColumns] = React.useState<Set<ColumnKey>>(DEFAULT_VISIBLE);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["findings-tabular", issueTag, forum, search],
    queryFn: () =>
      listFindings({
        issue_tag: issueTag || undefined,
        forum: (forum || undefined) as Finding["forum"] | undefined,
        search: search || undefined,
        limit: 200,
      }),
  });

  function toggleColumn(key: ColumnKey) {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const columns: DataTableColumn<Finding>[] = [
    {
      key: "case_number",
      header: "Order",
      accessor: (row) => (
        <div>
          <div className="font-mono font-medium tabular-nums text-zinc-900">{row.case_number}</div>
          <div className="text-xs text-zinc-400">{humanizeForum(row.forum)}</div>
        </div>
      ),
      sortValue: (row) => row.case_number,
    },
    {
      key: "decision_date",
      header: "Decided",
      accessor: (row) => formatDate(row.decision_date),
      sortValue: (row) => row.decision_date,
    },
    {
      key: "outcome",
      header: "Outcome",
      accessor: (row) => (
        <span className={row.outcome === "allowed" ? "font-medium text-emerald-700" : "font-medium text-red-700"}>
          {humanizeIssueTag(row.outcome)}
        </span>
      ),
      sortValue: (row) => row.outcome,
    },
  ];

  if (visibleColumns.has("petitioner_respondent")) {
    columns.push({
      key: "parties",
      header: "Petitioner / Respondent",
      accessor: (row) => (
        <span className="text-zinc-600">
          {row.petitioner} v. {row.respondent}
        </span>
      ),
    });
  }
  if (visibleColumns.has("quantum_amount")) {
    columns.push({
      key: "quantum_amount",
      header: "Quantum",
      accessor: (row) => <span className="font-mono tabular-nums">{formatINR(row.quantum_amount)}</span>,
      sortValue: (row) => row.quantum_amount ?? -1,
    });
  }
  if (visibleColumns.has("quantum_basis")) {
    columns.push({
      key: "quantum_basis",
      header: "Quantum basis",
      accessor: (row) => <span className="max-w-xs truncate text-zinc-600">{row.quantum_basis ?? "—"}</span>,
      className: "max-w-xs",
    });
  }
  if (visibleColumns.has("ratio_summary")) {
    columns.push({
      key: "ratio_summary",
      header: "Ratio summary",
      accessor: (row) => <span className="max-w-md text-zinc-600">{row.ratio_summary}</span>,
      className: "max-w-md",
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-zinc-900">Tabular Review</h1>
        <p className="mt-1 max-w-2xl text-sm text-zinc-500">
          Pick a set of comparable orders and see one field pulled out of each, side by side - useful for checking
          how a single parameter (say, a carrying-cost rate or a quantum basis) has actually been treated across
          rulings on the same issue.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl bg-white p-3 shadow-xs ring-1 ring-black/[0.07] sm:p-4">
        <label className="flex flex-col gap-1 text-xs font-medium text-zinc-500">
          Issue
          <Select value={issueTag} onChange={(event) => setIssueTag(event.target.value)} inputSize="sm" className="min-w-[10rem]">
            <option value="">All issues</option>
            {ISSUE_TAGS.map((tag) => (
              <option key={tag} value={tag}>
                {humanizeIssueTag(tag)}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-zinc-500">
          Forum
          <Select value={forum} onChange={(event) => setForum(event.target.value)} inputSize="sm" className="min-w-[10rem]">
            <option value="">All forums</option>
            {FORUMS.map((f) => (
              <option key={f} value={f}>
                {humanizeForum(f)}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex min-w-[12rem] flex-1 flex-col gap-1 text-xs font-medium text-zinc-500">
          Search
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Case number, petitioner, respondent..."
            inputSize="sm"
          />
        </label>
        <div className="flex flex-wrap gap-3 border-l border-zinc-200 pl-3">
          {OPTIONAL_COLUMNS.map((col) => (
            <label key={col.key} className="flex items-center gap-1.5 text-xs text-zinc-600">
              <input
                type="checkbox"
                checked={visibleColumns.has(col.key)}
                onChange={() => toggleColumn(col.key)}
              />
              {col.label}
            </label>
          ))}
        </div>
      </div>

      {isLoading ? <LoadingState label="Loading orders..." /> : null}
      {isError ? <ErrorState error={error} label="Could not load orders" /> : null}

      {data ? (
        <div className="overflow-hidden rounded-xl bg-white shadow-xs ring-1 ring-black/[0.07]">
          <DataTable
            columns={columns}
            data={data.items}
            rowKey={(row) => row.id}
            initialSort={{ key: "decision_date", direction: "desc" }}
            emptyMessage="No orders match these filters."
          />
          <div className="border-t border-zinc-100 px-4 py-2 text-xs text-zinc-400">
            {data.total} order(s) match{issueTag ? ` "${humanizeIssueTag(issueTag)}"` : ""}
          </div>
        </div>
      ) : null}
    </div>
  );
}
